import React, { useState, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { 
  Phone, AlertCircle, Bot, ExternalLink, X, 
  PhoneCall, MessageCircle, Loader2
} from 'lucide-react';

const useAuth = () => useContext(AuthContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function HelplineButton({ maskedId, className = '' }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleHelplineClick = async () => {
    setShowModal(true);
    setLoading(true);
    setResult(null);
    
    try {
      // First check availability
      const availRes = await fetch(`${API_URL}/api/mvp/helpline/availability`);
      const availability = await availRes.json();
      
      if (availability.humans_available) {
        // Request helpline session
        const reqRes = await fetch(
          `${API_URL}/api/mvp/helpline/request?user_id=${user.id}&masked_id=${maskedId}`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        const reqData = await reqRes.json();
        setResult({
          status: 'connected',
          session_id: reqData.session_id,
          message: 'Connecting you to a counselor...'
        });
      } else {
        // Offline - show fallback
        const reqRes = await fetch(
          `${API_URL}/api/mvp/helpline/request?user_id=${user.id}&masked_id=${maskedId}`,
          {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        const reqData = await reqRes.json();
        setResult({
          status: 'offline',
          ...reqData,
          ...availability
        });
      }
    } catch (err) {
      console.error('Helpline error:', err);
      setResult({
        status: 'error',
        message: 'Unable to connect. Please try again or call emergency services.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleJoinSession = () => {
    if (result?.session_id) {
      navigate(`/chat/${result.session_id}`);
    }
    setShowModal(false);
  };

  return (
    <>
      <button
        onClick={handleHelplineClick}
        className={`flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-xl font-semibold shadow-lg hover:from-red-600 hover:to-orange-600 transition-all ${className}`}
      >
        <Phone size={20} />
        Need help now? (Helpline)
      </button>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Phone className="text-red-500" size={20} />
                Helpline Support
              </h2>
              <button onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>

            {loading ? (
              <div className="py-8 text-center">
                <Loader2 className="mx-auto animate-spin text-blue-500 mb-3" size={40} />
                <p className="text-gray-600">Checking availability...</p>
              </div>
            ) : result?.status === 'connected' ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <PhoneCall className="text-green-600" size={32} />
                </div>
                <h3 className="text-lg font-semibold text-green-700 mb-2">Counselor Available!</h3>
                <p className="text-gray-600 mb-6">{result.message}</p>
                <button
                  onClick={handleJoinSession}
                  className="w-full py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700"
                >
                  Start Chat Now
                </button>
              </div>
            ) : result?.status === 'offline' ? (
              <div className="py-4">
                {/* Caregiver Unavailable Message */}
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-5 mb-4">
                  <p className="text-blue-800 font-medium mb-3">
                    Your chosen care-giver is currently unavailable.
                  </p>
                  <p className="text-blue-700 text-sm mb-4">
                    Let us connect you to a different caregiver while you wait. You will be connected shortly.
                  </p>
                  <div className="bg-white/70 rounded-lg p-4">
                    <p className="text-gray-700 text-sm mb-2">
                      While you wait, may we request you to take a deep breath.
                    </p>
                    <p className="text-gray-600 text-sm italic">
                      Did that? <span className="font-medium text-green-600">Excellent!</span> You have already done something important by showing up today.
                    </p>
                  </div>
                </div>

                {/* Chatbot Option */}
                {result.chatbot_enabled && result.chatbot_url && (
                  <div className="bg-purple-50 rounded-xl p-4 mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Bot className="text-purple-600" size={24} />
                      <div>
                        <p className="font-medium text-purple-800">24×7 Digital Assistant</p>
                        <p className="text-sm text-purple-600">Get instant support from our chatbot</p>
                      </div>
                    </div>
                    <a
                      href={result.chatbot_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700"
                    >
                      <MessageCircle size={18} />
                      Talk to Digital Assistant
                      <ExternalLink size={14} />
                    </a>
                  </div>
                )}

                {/* Emergency Contacts */}
                <div className="bg-red-50 rounded-xl p-4">
                  <p className="text-sm font-medium text-red-800 mb-2">Emergency Contacts:</p>
                  <div className="flex items-center gap-2 mb-2">
                    <Phone className="text-red-600" size={16} />
                    <a href={`tel:${result.tele_manas_number}`} className="text-red-700 font-semibold">
                      Tele-MANAS: {result.tele_manas_number}
                    </a>
                  </div>
                  {result.emergency_contact_text && (
                    <p className="text-sm text-red-700">{result.emergency_contact_text}</p>
                  )}
                </div>
              </div>
            ) : result?.status === 'error' ? (
              <div className="py-4 text-center">
                <AlertCircle className="mx-auto text-red-500 mb-3" size={40} />
                <p className="text-gray-600">{result.message}</p>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </>
  );
}
